import EmptyState from "@/components/EmptyState";
import SearchInput from "@/components/SearchInput";
import Table, {
  TableControls,
  TableFooter,
  toTableControlActions,
  buildAvatarDetailsColumn,
  buildCopyTextColumn,
  buildActionColumn
} from "@/components/Table";
import useTableRowMenu, { actionId } from "@/hooks/useTableRowMenu";
import { toFormat } from "@/lib/dateHelpers";
import { useQuery, useMutation } from "@apollo/client";
import { useLocation, useNavigate } from "react-router-dom";
import { generatePath } from "react-router-dom";
import { Path } from "@/router";
import { useIsCompactLayout } from "@/hooks/useIsCompactLayout";
import { useSearchWithParams } from "@/hooks/useSearchParams";
import { filterByClientSearch } from "@/lib/clientSearch";
import { renderEmptyCellState } from "@/components/Table/utils";
import { useMemo, useState } from "react";
import PersonSideSheet from "@/features/person/components/PersonSideSheet/PersonSideSheet";
import { AddPersonModal } from "./_AddPersonModal";
import { RemovePersonDialog } from "./_RemovePersonDialog";
import {
  UPSERT_FACILITY_ROLE_MUTATION,
  UpsertFacilityRoleResponse,
  UpsertFacilityRoleVariables,
  DELETE_FACILITY_ROLE_MUTATION,
  DeleteFacilityRoleResponse,
  DeleteFacilityRoleVariables
} from "./mutations";
import { showErrorSnackbar, showSnackbar } from "@/lib/snackbarUtils";
import { FACILITY_PERMISSIONS_QUERY, type FacilityOperation, type FacilityPermissionsQueryResponse } from "./queries";

export function FacilityPermissionsTable({ facilityId }: { facilityId: string }) {
  const { data, loading } = useQuery<FacilityPermissionsQueryResponse>(FACILITY_PERMISSIONS_QUERY, {
    variables: { facilityId }
  });

  const location = useLocation();
  const navigate = useNavigate();
  const { Menu, setMenu } = useTableRowMenu();
  const { isCompactLayout } = useIsCompactLayout();
  const [isAddPersonModalOpen, setIsAddPersonModalOpen] = useState(false);
  const [isRemovePersonDialogOpen, setIsRemovePersonDialogOpen] = useState(false);
  const [personToRemove, setPersonToRemove] = useState<FacilityOperation | null>(null);
  const [isPersonSideSheetOpen, setIsPersonSideSheetOpen] = useState(false);
  const [selectedPgaId, setSelectedPgaId] = useState<string | undefined>(undefined);

  const [upsertFacilityRole, { loading: upsertingRole }] = useMutation<
    UpsertFacilityRoleResponse,
    UpsertFacilityRoleVariables
  >(UPSERT_FACILITY_ROLE_MUTATION, {
    onCompleted: (data) => {
      if (data?.upsertFacilityRole?.success) {
        showSnackbar(data.upsertFacilityRole.message || "Person added to facility permissions");
      } else {
        showErrorSnackbar(data?.upsertFacilityRole?.message || "Failed to add person to facility permissions");
      }
    },
    onError: (_error) => {
      showErrorSnackbar("Failed to add person to facility permissions");
    },
    refetchQueries: [
      {
        query: FACILITY_PERMISSIONS_QUERY,
        variables: { facilityId }
      }
    ],
    awaitRefetchQueries: true
  });

  const [deleteFacilityRole, { loading: deletingRole }] = useMutation<
    DeleteFacilityRoleResponse,
    DeleteFacilityRoleVariables
  >(DELETE_FACILITY_ROLE_MUTATION, {
    onCompleted: (data) => {
      if (data?.deleteFacilityRole?.success) {
        showSnackbar(data.deleteFacilityRole.message || "Person removed from facility permissions");
        setIsRemovePersonDialogOpen(false);
        setPersonToRemove(null);
      } else {
        showErrorSnackbar(data?.deleteFacilityRole?.message || "Failed to remove person from facility permissions");
      }
    },
    onError: (_error) => {
      showErrorSnackbar("Failed to remove person from facility permissions");
    },
    refetchQueries: [
      {
        query: FACILITY_PERMISSIONS_QUERY,
        variables: { facilityId }
      }
    ],
    awaitRefetchQueries: true
  });

  const permissions = data?.facility.roles.nodes;
  const { searchValue, setSearchValue, clearSearch } = useSearchWithParams({ paramName: "search" });
  const hasSearch = !!searchValue.trim();
  const visiblePermissions = useMemo(
    () =>
      filterByClientSearch(permissions ?? [], searchValue, (row) => [
        row.person.displayName,
        row.person.pgaId,
        row.role,
        row.person.latestProgram?.programType?.name,
        row.updatedAt ? toFormat(row.updatedAt) : null
      ]),
    [permissions, searchValue]
  );

  function buildPersonPath(pgaId: string | null | undefined): Path | null {
    if (!pgaId) {
      return null;
    }
    const returnTo = location.pathname;
    return generatePath(`/people/:pgaId/person/overview?return_to=${encodeURIComponent(returnTo)}`, { pgaId }) as Path;
  }

  const handleAddPerson = async (personId: string, role: string) => {
    try {
      await upsertFacilityRole({
        variables: {
          contactId: personId,
          facilityId: facilityId,
          role: role
        }
      });

      setIsAddPersonModalOpen(false);
    } catch (error) {
      console.error("Error adding person to facility permissions:", error);
    }
  };

  const handleRemovePerson = async () => {
    if (!personToRemove) return;

    try {
      await deleteFacilityRole({
        variables: {
          roleId: personToRemove.id
        }
      });
    } catch (error) {
      console.error("Error removing person from facility permissions:", error);
    }
  };

  const openRemoveDialog = (operation: FacilityOperation) => {
    setPersonToRemove(operation);
    setIsRemovePersonDialogOpen(true);
  };

  const columns = [
    ...buildAvatarDetailsColumn<FacilityOperation>({
      isCompactLayout,
      getTitle: (row) => row.person.displayName,
      getSubtitle: (row) => row.person.latestProgram?.programType?.name || "",
      subtitleLabel: "Program",
      getPicture: (row) => row.person.profilePhoto,
      header: "Name",
      size: 230
    }),
    buildCopyTextColumn<FacilityOperation>({
      accessorKey: "person.pgaId",
      header: "PGA ID",
      copyFieldLabel: "PGA ID",
      getText: (row) => row.person.pgaId || "",
      shouldShowCopyButton: (row) => !!row.person.pgaId,
      enableSorting: false
    }),
    {
      accessorFn: (row: FacilityOperation) => row.role,
      header: "Role",
      id: "role",
      enableSorting: false
    },
    {
      accessorFn: (row: FacilityOperation) => row.updatedAt,
      header: "Modified",
      id: "updatedAt",
      enableSorting: false,
      cell: ({ getValue }: { getValue: () => string }) => {
        const date = getValue();
        return renderEmptyCellState(date ? toFormat(date) : null);
      }
    },
    buildActionColumn<FacilityOperation & { id: string }>({
      getActions: (row) => [
        {
          text: "View Person",
          section: "Actions",
          onClick: () => {
            const personPath = buildPersonPath(row.person.pgaId);
            if (personPath) {
              navigate(personPath);
            }
          }
        },
        {
          text: "Remove from Facility Permissions",
          iconName: "delete",
          destructive: true,
          onClick: () => openRemoveDialog(row)
        }
      ],
      onViewClick: (row) => {
        if (row.person.pgaId) {
          setSelectedPgaId(row.person.pgaId);
          setIsPersonSideSheetOpen(true);
        }
      },
      viewButtonTitle: "View person details",
      actionId,
      setMenu,
      getRowId: (row) => row.id
    })
  ];

  return (
    <>
      <Menu />
      <div className="pt-12" />
      <Table
        data={visiblePermissions}
        loading={loading}
        getRowLink={(row) => {
          const personPath = buildPersonPath(row.original.person.pgaId);
          return personPath ? { to: personPath } : null;
        }}
        columns={columns}
        hasActiveFilters={hasSearch}
        renderControls={(_table, { onSortClick } = {}) => (
          <TableControls
            actions={toTableControlActions([
              {
                label: "Add Person",
                icon: "add",
                variant: "filled",
                onClick: () => setIsAddPersonModalOpen(true)
              }
            ])}
            onSortClick={onSortClick}
          >
            <SearchInput
              placeholder="Search People"
              query={searchValue}
              onChange={setSearchValue}
              onClear={clearSearch}
            />
          </TableControls>
        )}
        renderNoDataEmptyState={() => (
          <EmptyState
            title="No Permissions"
            caption="There are no people with roles at this facility at this time"
            iconName="person"
          />
        )}
        renderFilteredEmptyState={() => (
          <EmptyState title="No Results Found" caption="Try a different search." iconName="search" />
        )}
        renderFooter={() => <TableFooter totalRowCount={visiblePermissions.length} />}
      />
      <PersonSideSheet
        open={isPersonSideSheetOpen}
        onClose={() => {
          setIsPersonSideSheetOpen(false);
          setSelectedPgaId(undefined);
        }}
        pgaId={selectedPgaId ?? null}
        showEmployment={true}
        showFacility={true}
      />

      <AddPersonModal
        open={isAddPersonModalOpen}
        onClose={() => setIsAddPersonModalOpen(false)}
        onSave={handleAddPerson}
        isLoading={upsertingRole}
      />

      <RemovePersonDialog
        open={isRemovePersonDialogOpen}
        onClose={() => {
          setIsRemovePersonDialogOpen(false);
          setPersonToRemove(null);
        }}
        onConfirm={handleRemovePerson}
        isLoading={deletingRole}
        personName={personToRemove?.person.displayName}
        roleTitle={personToRemove?.role}
      />
    </>
  );
}
